import { DeleteOutlined, EditOutlined, PlusOutlined, SafetyOutlined } from '@ant-design/icons';
import {
    Alert,
    Button,
    Empty,
    Modal,
    Select,
    Space,
    Tag,
    Tooltip,
    Typography,
    message,
} from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { staticMenuItems } from '../config/menuConfig';
import {
    categoryErrorKey,
    inMenuOrder,
    retainKnownPages,
    sameKeySet,
    togglePageKey,
    type UserCategory,
} from '../domain/categoryPermissions';
import {
    menuLabelKey,
    pageKeyOf,
    permissionableItems,
    resolveMenuLabel,
} from '../domain/pagePermissions';
import useRequest from '../hooks/useRequest';
import CategoryFormModal, { type CategoryFormValues } from './permissions/CategoryFormModal';
import PagePreview from './permissions/PagePreview';
import PermissionMenuList from './permissions/PermissionMenuList';

const { Title, Text } = Typography;

/** The pages the administrator can grant, in menu order. The menu is the one list of pages. */
const GRANTABLE_ITEMS = permissionableItems(staticMenuItems);
const GRANTABLE_PAGE_KEYS = GRANTABLE_ITEMS.map(item => pageKeyOf(item.path));

interface CategoriesResponse {
    data: UserCategory[];
}

interface CategoryResponse {
    data: UserCategory;
}

type FormMode = 'closed' | 'create' | 'edit';

/**
 * Configuration > User categories (administrators only).
 *
 * Every user other than an administrator belongs to a category, and the category lists the pages
 * its members may open. The administrator picks a category, ticks the pages in a copy of the menu
 * and can click any page to see it in a small preview before deciding. Which category a user is in
 * is set on the Users tab.
 */
const UserCategoryPermissions = () => {
    const { t } = useTranslation();
    const request = useRequest();
    const [categories, setCategories] = useState<UserCategory[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [draftPages, setDraftPages] = useState<string[]>([]);
    const [previewedKey, setPreviewedKey] = useState<string | null>(null);
    const [formMode, setFormMode] = useState<FormMode>('closed');

    const selected = categories.find(category => category.id === selectedId) ?? null;
    const savedPages = useMemo(
        () => retainKnownPages(selected?.pageKeys ?? [], GRANTABLE_PAGE_KEYS),
        [selected],
    );
    const dirty = selected !== null && !sameKeySet(draftPages, savedPages);

    const previewedItem = GRANTABLE_ITEMS.find(item => pageKeyOf(item.path) === previewedKey);
    const previewedAllowed = previewedKey !== null && draftPages.includes(previewedKey);
    const previewedLabelKey = previewedItem ? menuLabelKey(previewedItem.path) : '';
    const previewedLabel = previewedItem
        ? resolveMenuLabel(t(previewedLabelKey), previewedLabelKey, previewedItem.name)
        : '';

    const showError = useCallback(
        (error: unknown, fallbackKey: string) => {
            console.error(fallbackKey, error);
            message.error(t(categoryErrorKey(error, fallbackKey)));
        },
        [t],
    );

    const applySelection = useCallback((category: UserCategory | undefined) => {
        setSelectedId(category?.id ?? null);
        setDraftPages(retainKnownPages(category?.pageKeys ?? [], GRANTABLE_PAGE_KEYS));
    }, []);

    const loadCategories = useCallback(
        async (preferredId?: number) => {
            try {
                setLoading(true);
                const response = await request<CategoriesResponse>({ path: '/user-categories' });
                const loaded = response.data ?? [];
                setCategories(loaded);
                applySelection(loaded.find(category => category.id === preferredId) ?? loaded[0]);
            } catch (error) {
                showError(error, 'configuration.permissions.loadError');
            } finally {
                setLoading(false);
            }
        },
        [request, applySelection, showError],
    );

    useEffect(() => {
        void loadCategories();
    }, [loadCategories]);

    const requestSelect = (categoryId: number) => {
        const target = categories.find(category => category.id === categoryId);
        if (!dirty) {
            applySelection(target);
            return;
        }

        Modal.confirm({
            title: t('configuration.permissions.discardTitle'),
            content: t('configuration.permissions.discardContent'),
            okText: t('configuration.permissions.discard'),
            cancelText: t('configuration.permissions.cancel'),
            onOk: () => applySelection(target),
        });
    };

    const replaceCategory = (saved: UserCategory) => {
        setCategories(current =>
            current.map(category => (category.id === saved.id ? saved : category)),
        );
    };

    const savePages = async () => {
        if (!selected) {
            return;
        }

        try {
            setSaving(true);
            const response = await request<CategoryResponse>({
                path: `/user-categories/${selected.id}`,
                method: 'PUT',
                params: {
                    name: selected.name,
                    description: selected.description,
                    pageKeys: inMenuOrder(draftPages, GRANTABLE_PAGE_KEYS),
                },
            });
            replaceCategory(response.data);
            applySelection(response.data);
            message.success(t('configuration.permissions.saveSuccess'));
        } catch (error) {
            showError(error, 'configuration.permissions.saveError');
        } finally {
            setSaving(false);
        }
    };

    const submitCategoryForm = async (values: CategoryFormValues) => {
        const creating = formMode === 'create';

        try {
            setSaving(true);
            // Renaming keeps the pages as saved: an unsaved ticking is not part of the rename.
            const response = await request<CategoryResponse>({
                path: creating ? '/user-categories' : `/user-categories/${selected?.id}`,
                method: creating ? 'POST' : 'PUT',
                params: {
                    name: values.name,
                    description: values.description,
                    pageKeys: creating ? [] : savedPages,
                },
            });
            setFormMode('closed');

            if (creating) {
                setCategories(current => [...current, response.data]);
                applySelection(response.data);
            } else {
                replaceCategory(response.data);
            }
            message.success(
                t(
                    creating
                        ? 'configuration.permissions.createSuccess'
                        : 'configuration.permissions.updateSuccess',
                ),
            );
        } catch (error) {
            showError(error, 'configuration.permissions.saveError');
        } finally {
            setSaving(false);
        }
    };

    const deleteSelected = () => {
        if (!selected) {
            return;
        }

        Modal.confirm({
            title: t('configuration.permissions.deleteTitle'),
            content: t('configuration.permissions.deleteContent', { name: selected.name }),
            okText: t('configuration.permissions.deleteCategory'),
            okType: 'danger',
            cancelText: t('configuration.permissions.cancel'),
            onOk: async () => {
                try {
                    await request({ path: `/user-categories/${selected.id}`, method: 'DELETE' });
                    message.success(t('configuration.permissions.deleteSuccess'));
                    await loadCategories();
                } catch (error) {
                    showError(error, 'configuration.permissions.deleteError');
                }
            },
        });
    };

    const categoryOptions = categories.map(category => ({
        value: category.id,
        label: `${category.name} (${t('configuration.permissions.members', { count: category.memberCount })})`,
    }));
    const deleteBlocked = (selected?.memberCount ?? 0) > 0;
    const controlsDisabled = selected === null || saving;

    return (
        <div data-testid="user-categories">
            <Title level={4}>
                <SafetyOutlined style={{ marginRight: 8 }} />
                {t('configuration.permissions.title')}
            </Title>
            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={t('configuration.permissions.description')}
            />

            <Space wrap style={{ marginBottom: 16 }}>
                <Select
                    style={{ minWidth: 280 }}
                    loading={loading}
                    value={selectedId}
                    options={categoryOptions}
                    onChange={requestSelect}
                    placeholder={t('configuration.permissions.selectCategory')}
                    data-testid="category-select"
                />
                <Button
                    icon={<PlusOutlined />}
                    onClick={() => setFormMode('create')}
                    data-testid="category-new"
                >
                    {t('configuration.permissions.newCategory')}
                </Button>
                <Button
                    icon={<EditOutlined />}
                    onClick={() => setFormMode('edit')}
                    disabled={controlsDisabled}
                    data-testid="category-edit"
                >
                    {t('configuration.permissions.editCategory')}
                </Button>
                <Tooltip title={deleteBlocked ? t('configuration.permissions.deleteBlocked') : ''}>
                    <span>
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={deleteSelected}
                            disabled={controlsDisabled || deleteBlocked}
                            data-testid="category-delete"
                        >
                            {t('configuration.permissions.deleteCategory')}
                        </Button>
                    </span>
                </Tooltip>
            </Space>

            {selected === null ? (
                <Empty
                    description={t('configuration.permissions.noCategories')}
                    data-testid="categories-empty"
                />
            ) : (
                <>
                    {selected.description && (
                        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                            {selected.description}
                        </Text>
                    )}
                    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                        <PermissionMenuList
                            items={GRANTABLE_ITEMS}
                            selected={draftPages}
                            previewedKey={previewedKey}
                            disabled={saving}
                            onToggle={(pageKey, checked) =>
                                setDraftPages(current => togglePageKey(current, pageKey, checked))
                            }
                            onPreview={setPreviewedKey}
                            onSelectAll={() => setDraftPages([...GRANTABLE_PAGE_KEYS])}
                            onClearAll={() => setDraftPages([])}
                        />
                        <div style={{ flex: 1, minWidth: 360 }}>
                            <Space style={{ marginBottom: 8 }} wrap>
                                <Text strong>{t('configuration.permissions.previewTitle')}</Text>
                                {previewedItem && (
                                    <>
                                        <Text>{previewedLabel}</Text>
                                        <Tag
                                            color={previewedAllowed ? 'green' : 'default'}
                                            data-testid="preview-status"
                                        >
                                            {previewedAllowed
                                                ? t('configuration.permissions.allowed')
                                                : t('configuration.permissions.notAllowed')}
                                        </Tag>
                                    </>
                                )}
                            </Space>
                            <PagePreview
                                path={previewedItem?.path ?? null}
                                title={previewedLabel}
                            />
                        </div>
                    </div>
                    <Space style={{ marginTop: 16 }} wrap>
                        <Button
                            type="primary"
                            onClick={savePages}
                            loading={saving}
                            disabled={!dirty}
                            data-testid="permissions-save"
                        >
                            {t('configuration.permissions.save')}
                        </Button>
                        <Button
                            onClick={() => applySelection(selected)}
                            disabled={!dirty || saving}
                            data-testid="permissions-discard"
                        >
                            {t('configuration.permissions.discard')}
                        </Button>
                        {dirty && (
                            <Text type="warning" data-testid="permissions-unsaved">
                                {t('configuration.permissions.unsaved')}
                            </Text>
                        )}
                    </Space>
                    <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
                        {t('configuration.permissions.adminNote')}
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                        {t('configuration.permissions.sameRolesNote')}
                    </Text>
                </>
            )}

            <CategoryFormModal
                open={formMode !== 'closed'}
                initialValues={
                    formMode === 'edit' && selected
                        ? { name: selected.name, description: selected.description }
                        : null
                }
                saving={saving}
                onSubmit={submitCategoryForm}
                onCancel={() => setFormMode('closed')}
            />
        </div>
    );
};

export default UserCategoryPermissions;
